import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { AccountGate, AccountPicker } from 'components/account/AccountGate';
import { BrandIcon } from 'components/brand/BrandIcon';
import { NoCampaigns } from 'components/campaign/NoCampaigns';
import { WidgetCard } from 'components/widgets/WidgetCard';
import { WidgetForm } from 'components/widgets/WidgetForm';
import { WixElement, createApi } from 'lib/gb-api';
import { getDashboardAccessToken } from 'lib/wix-dashboard-sdk';

type View =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit'; displayId: string }
  | { kind: 'switch-account' };

export const Dashboard = () => {
  const api = useMemo(() => createApi(getDashboardAccessToken), []);
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>({ kind: 'list' });

  const instanceQuery = useQuery({
    queryKey: ['wix', 'instance'],
    queryFn: api.decodeInstance,
    refetchOnWindowFocus: true,
  });

  const elementsQuery = useQuery({
    queryKey: ['wix', 'elements'],
    queryFn: api.listElements,
    enabled: instanceQuery.data?.is_linked === true,
  });

  const campaignsQuery = useQuery({
    queryKey: ['wix', 'campaigns'],
    queryFn: api.listCampaigns,
    enabled: instanceQuery.data?.is_linked === true,
  });

  const noCampaigns = campaignsQuery.isSuccess && campaignsQuery.data.length === 0;

  const deleteMutation = useMutation({
    mutationFn: api.deleteElement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] }),
  });

  const disconnectMutation = useMutation({
    mutationFn: api.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wix', 'instance'] });
      setView({ kind: 'list' });
    },
  });

  const listError = (disconnectMutation.error || deleteMutation.error) as {
    response?: { data?: { message?: string } };
  } | null;

  if (instanceQuery.isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-sm text-gray-500">
        <BrandIcon size={48} className="animate-pulse" />
        Loading…
      </div>
    );
  }

  if (instanceQuery.error || !instanceQuery.data) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div className="flex max-w-sm flex-col items-center gap-4">
          <BrandIcon size={56} />
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-semibold text-gray-900">Couldn&rsquo;t load app context</h1>
            <p className="text-sm text-gray-500">
              We weren&rsquo;t able to verify this Wix install. Try reopening the app from your Wix
              dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const instance = instanceQuery.data;

  if (!instance.is_linked) {
    return (
      <div className="relative flex min-h-full items-center justify-center overflow-hidden p-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-[#fff6da] to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-12 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-butter/20 blur-3xl"
        />
        <div className="relative z-10 flex w-full justify-center">
          <AccountGate api={api} instance={instance} />
        </div>
      </div>
    );
  }

  if (view.kind === 'create') {
    return (
      <WidgetForm
        api={api}
        accountAid={instance.account_aid ?? ''}
        accountId={instance.account_id}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] });
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  if (view.kind === 'edit') {
    return (
      <WidgetForm
        api={api}
        accountAid={instance.account_aid ?? ''}
        accountId={instance.account_id}
        editingDisplayId={view.displayId}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] });
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  if (view.kind === 'switch-account') {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-8">
        <header className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold text-gray-900">Switch account</h1>
          <Button variant="outline" color="neutral" onClick={() => setView({ kind: 'list' })}>
            Back
          </Button>
        </header>
        <section className="max-w-md">
          <AccountPicker
            api={api}
            selectedAccountId={instance.account_id}
            onSuccess={() => setView({ kind: 'list' })}
          />
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-8">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BrandIcon size={44} />
          <div className="flex flex-col gap-0.5">
            <h1 className="text-2xl font-semibold text-gray-900">Widgets</h1>
            <div className="flex flex-col gap-1">
              <div className="flex max-w-96 flex-wrap gap-1 text-xs text-gray-500">
                <p>{instance.site?.site_name ?? 'Wix site'}</p>·
                <p>
                  {instance.account_name} (Account #{instance.account_id})
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="transparent"
            color="neutral"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={() => setView({ kind: 'create' })}
            disabled={noCampaigns}
          >
            Create widget
          </Button>
        </div>
      </header>

      {listError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {listError?.response?.data?.message ?? 'Something went wrong. Try again.'}
        </div>
      )}

      {noCampaigns ? (
        <NoCampaigns accountId={instance.account_id ?? null} />
      ) : (
        <WidgetList
          elements={elementsQuery.data ?? []}
          isLoading={elementsQuery.isLoading}
          onEdit={displayId => setView({ kind: 'edit', displayId })}
          onDelete={displayId => {
            deleteMutation.mutate(displayId);
          }}
          onCreate={() => setView({ kind: 'create' })}
        />
      )}
    </div>
  );
};

interface WidgetListProps {
  elements: WixElement[];
  isLoading: boolean;
  onEdit: (displayId: string) => void;
  onDelete: (displayId: string) => void;
  onCreate: () => void;
}

const WidgetList = ({ elements, isLoading, onEdit, onDelete, onCreate }: WidgetListProps) => {
  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading widgets…</p>;
  }

  if (elements.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <BrandIcon size={56} />
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-gray-900">No widgets yet</h2>
          <p className="max-w-md text-sm text-gray-500">
            Create your first donation widget to start collecting on your Wix site.
          </p>
        </div>
        <Button variant="solid" color="primary" onClick={onCreate}>
          Create your first widget
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {elements.map(el => (
        <WidgetCard
          key={el.id}
          element={el}
          onEdit={() => onEdit(el.display_id)}
          onDelete={() => onDelete(el.display_id)}
        />
      ))}
    </div>
  );
};
